import { useContext, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'

import { appConfig } from '@/config'
import { useOrganization } from '@/contexts/OrganizationContext'
import { ContentType, InternalApi, RequestParams } from '@api/generated/internal/internalApi'
import { AuthContext } from 'torqcloud-ui'

const BASE_URL = `${appConfig.flowiseBaseUrl}/api/v1`

const ORG_SCOPED_PREFIXES = ['/teammates', '/teams', '/user-tools', '/memory']

export function formatAgentApiFormData(input: unknown): FormData {
  if (input instanceof FormData) return input
  if (typeof input !== 'object' || input === null) throw new Error('Input must be an object or FormData instance')

  return Object.keys(input).reduce((formData, key) => {
    const property = input[key]
    for (const value of Array.isArray(property) ? property : [property]) {
      formData.append(key, value instanceof Blob ? value : typeof value === 'object' && value !== null ? JSON.stringify(value) : `${value}`)
    }
    return formData
  }, new FormData())
}

export default function useAgentApi() {
  const context = useContext(AuthContext)
  const { currentOrganizationId } = useOrganization()
  const orgId = useRef(currentOrganizationId)
  orgId.current = currentOrganizationId

  const navigate = useNavigate()
  const location = useLocation()
  const navigateRef = useRef(navigate)
  const pathnameRef = useRef(location.pathname)
  navigateRef.current = navigate
  pathnameRef.current = location.pathname

  const client = useMemo(() => {
    const internalApi = new InternalApi<void>({
      baseUrl: BASE_URL,
      baseApiParams: {
        secure: true,
      },
      securityWorker: async (): Promise<RequestParams> => {
        const session = await context.authenticationManager.currentSession()
        const token = session?.getIdToken()?.getJwtToken()
        const headers: Record<string, string> = {}

        if (token) {
          headers.Authorization = `Bearer ${token}`
        }

        if (orgId.current) {
          headers['x-organizationId'] = orgId.current
        }

        return { headers }
      },
      customFetch: async (...args: Parameters<typeof fetch>) => {
        const response = await fetch(...args)
        if (response.status === 403) {
          try {
            const body = await response.clone().json()
            if (body?.code === 'FORBIDDEN' && body?.details?.reason === 'organization_mismatch') {
              const pathname = pathnameRef.current
              const matchedPrefix = ORG_SCOPED_PREFIXES.find(p => pathname.startsWith(p))
              const destination = matchedPrefix ?? '/'
              if (pathname !== destination) {
                navigateRef.current(destination)
              }
              toast.error('This resource belongs to a different organization. Switch organizations to access it.', {
                id: 'organization_mismatch',
              })
            }
          } catch {
            // not JSON or unrelated 403 — let it propagate normally
          }
        }
        return response
      },
    })

    // Patch the FormData formatter to support arrays https://github.com/acacode/swagger-typescript-api/issues/462
    const formatters: Record<ContentType, (input: unknown) => any> = internalApi['contentFormatters']
    formatters[ContentType.FormData] = formatAgentApiFormData

    return internalApi
  }, [context.authenticationManager])

  return client
}
